import { useState, useEffect } from 'react';
import { Container, Header, Form, Dropdown, Input, TextArea, Button, Message, Divider, Loader } from 'semantic-ui-react';
import { useQuery, useMutation, gql } from '@apollo/client';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { useIsAdmin } from '../../../hooks/isAdmin';
import { useBlocks } from '../../../hooks/useBlocks';
import { GET_ME } from '../../../queries/getMe';

const GET_BLOCK_TAGS = gql`
  query BlockTags {
    blockTags {
      id
      name
      code
    }
  }
`;

const SET_ORGANISATION_BLOCKS = gql`
  mutation SetOrganisationBlocks($currentBlockTagId: Int, $nextBlockTagId: Int) {
    setOrganisationBlocks(currentBlockTagId: $currentBlockTagId, nextBlockTagId: $nextBlockTagId) {
      currentBlockTagId
      nextBlockTagId
    }
  }
`;

const SEND_ANNOUNCEMENT = gql`
  mutation SendAnnouncement($kind: String!, $title: String!, $body: String!) {
    sendAnnouncement(kind: $kind, title: $title, body: $body) {
      id
    }
  }
`;

const ANNOUNCEMENT_RECIPIENT_COUNT = gql`
  query AnnouncementRecipientCount($kind: String!) {
    announcementRecipientCount(kind: $kind) {
      subscribers
      recipients
    }
  }
`;

const KIND_OPTIONS = [
  { key: 'APPLICATION', value: 'APPLICATION', text: 'Application Announcement' },
  { key: 'PRODUCT',     value: 'PRODUCT',     text: 'Product Announcement' },
];

const NONE = '__none__';

export const AdminSettings = () => {
  const isAdmin = useIsAdmin();
  const { currentBlockTagId, nextBlockTagId, loading: blocksLoading } = useBlocks();

  const { data: blockTagsData, loading: tagsLoading } = useQuery(GET_BLOCK_TAGS);
  const [setOrganisationBlocks, { loading: saving, error }] = useMutation(SET_ORGANISATION_BLOCKS);

  const [currentId, setCurrentId] = useState<number | null>(null);
  const [nextId, setNextId] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  // Push notification announcement
  const [kind, setKind] = useState<'APPLICATION' | 'PRODUCT'>('APPLICATION');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const [sendAnnouncement, { loading: sending, error: sendError }] = useMutation(SEND_ANNOUNCEMENT);

  const { data: countData, loading: countLoading } = useQuery(ANNOUNCEMENT_RECIPIENT_COUNT, {
    variables: { kind },
  });

  useEffect(() => {
    if (!blocksLoading) {
      setCurrentId(currentBlockTagId);
      setNextId(nextBlockTagId);
    }
  }, [blocksLoading, currentBlockTagId, nextBlockTagId]);

  if (!isAdmin) return null;

  const tags = [...((blockTagsData?.blockTags ?? []) as { id: number; name: string; code: string }[])]
    .sort((a, b) => a.id - b.id);

  const tagOptions = tags.map((t) => ({
    key: t.id,
    value: t.id,
    text: `${t.name} (${t.code})`,
  }));

  const nextTagOptions = [
    { key: 'none', value: NONE, text: '— none —' },
    ...tagOptions,
  ];

  const handleSave = async () => {
    setSaved(false);
    await setOrganisationBlocks({
      variables: { currentBlockTagId: currentId, nextBlockTagId: nextId },
      refetchQueries: [{ query: GET_ME }],
    });
    setSaved(true);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return;
    await sendAnnouncement({ variables: { kind, title, body } });
    setSent(true);
    setTitle('');
    setBody('');
  };

  const count = countData?.announcementRecipientCount as { subscribers: number; recipients: number } | undefined;

  const recipientSummary = () => {
    if (countLoading) {
      return <span><Loader active inline size="tiny" /> Counting recipients…</span>;
    }
    if (!count || count.subscribers === 0) {
      return <span>No subscribed app users yet for this type.</span>;
    }
    return (
      <span>
        ≈ <strong>{count.recipients}</strong> app user{count.recipients === 1 ? '' : 's'} will receive this
        {' '}— {count.subscribers} subscribed, {count.recipients} with the iOS app installed.
      </span>
    );
  };

  return (
    <ResponsiveContainer>
      <Container style={{ paddingTop: '2em', maxWidth: '600px' }}>
        <Header as="h1">Block Settings</Header>
        <Form success={saved} error={!!error}>
          <Form.Field>
            <label>Current block</label>
            <Dropdown
              selection
              loading={tagsLoading || blocksLoading}
              options={tagOptions}
              value={currentId ?? undefined}
              placeholder="Select current block..."
              onChange={(_, { value }) => {
                const v = value as number;
                setCurrentId(v);
                if (v === nextId) setNextId(null);
                setSaved(false);
              }}
            />
          </Form.Field>
          <Form.Field>
            <label>Next block</label>
            <Dropdown
              selection
              loading={tagsLoading || blocksLoading}
              options={nextTagOptions}
              value={nextId != null ? nextId : NONE}
              onChange={(_, { value }) => {
                setNextId(value === NONE ? null : (value as number));
                setSaved(false);
              }}
            />
          </Form.Field>
          {saved && <Message success content="Settings saved." />}
          {error && <Message error content={error.message} />}
          <Button primary loading={saving} onClick={handleSave}>
            Save
          </Button>
        </Form>

        <Divider section />

        <Header as="h2">
          Push Notifications
          <Header.Subheader>
            Send a push notification to app users in supporting organisations who are
            subscribed to this topic, on both iOS and Android. Only users with the app
            installed and notifications enabled will receive it.
          </Header.Subheader>
        </Header>
        <Form success={sent} error={!!sendError}>
          <Form.Field>
            <label>Type</label>
            <Dropdown
              selection
              options={KIND_OPTIONS}
              value={kind}
              onChange={(_, { value }) => { setKind(value as 'APPLICATION' | 'PRODUCT'); setSent(false); }}
            />
            <div style={{ marginTop: '0.5em', color: 'rgba(0,0,0,0.6)' }}>
              {recipientSummary()}
            </div>
          </Form.Field>
          <Form.Field>
            <label>Title</label>
            <Input
              value={title}
              placeholder="Announcement title"
              onChange={(e) => { setTitle(e.target.value); setSent(false); }}
            />
          </Form.Field>
          <Form.Field>
            <label>Body</label>
            <TextArea
              value={body}
              placeholder="Announcement body"
              rows={4}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setBody(e.target.value); setSent(false); }}
            />
          </Form.Field>
          {sent && <Message success content="Announcement sent!" />}
          {sendError && <Message error content={sendError.message} />}
          <Button
            primary
            loading={sending}
            disabled={!title.trim() || !body.trim()}
            onClick={handleSend}
          >
            Send
          </Button>
        </Form>
      </Container>
    </ResponsiveContainer>
  );
};
